import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";

import ClubbieModal from "../Modal/ClubbieModal";
import ClubbieSelect from "../ClubbieSelect/ClubbieSelect";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { swapTeamInCategory } from "../../redux/slices/tournamentSlice";

const MODES = [
  {
    value: "INHERIT",
    label: "Inherit",
    description:
      "Played fixtures keep their results but get rewritten to the incoming team. The incoming team picks up the outgoing team's record.",
  },
  {
    value: "EXPUNGE",
    label: "Expunge",
    description:
      "Played fixtures are deleted (along with their events). The incoming team takes over upcoming fixtures only and starts at zero.",
  },
  {
    value: "FORFEIT",
    label: "Forfeit",
    description:
      "Played fixtures stay on the outgoing team with a forfeit score in favour of the opponent. The incoming team takes over upcoming fixtures only.",
  },
];

const FieldLabel = ({ children }) => (
  <p className="text-[15px] font-medium text-[#FEFCEB] m-0">{children}</p>
);

// A fixture counts as "played" if any of the places a result can land carries
// one. Mirrors the fixtures hook's derivation (useFixtureState L278-L282) — the
// raw `hasResult` flag isn't set by every result-entry path, but the linked
// Event carries the score snapshot, so we check that too.
// Use `!= null` (not `!== undefined`): a score stored as `null` — e.g. on an
// event that never had a result — would otherwise read as "played" and make an
// untouched future fixture look like a completed match.
const fixtureIsPlayed = (f) =>
  f?.hasResult ||
  f?.status === "COMPLETED" ||
  f?.homeTeamScore != null ||
  f?.eventId?.homeTeam?.scores != null;

// Match a fixture to a team across every id shape it can carry: the populated
// actual-team ref, the resolved homeTeam/awayTeam object the fixtures hook
// attaches, and the raw id fields.
const fixtureInvolvesTeam = (f, idStr) => {
  const candidates = [
    f?.homeTeamActualId?._id || f?.homeTeamActualId,
    f?.homeTeam?._id,
    f?.homeTeamId,
    f?.awayTeamActualId?._id || f?.awayTeamActualId,
    f?.awayTeam?._id,
    f?.awayTeamId,
  ];
  return candidates.some((id) => id && id.toString() === idStr);
};

// Flatten a category's competition-structure fixtures. Used only as a fallback
// when the page's already-derived fixture list isn't supplied.
const flattenCategoryFixtures = (category) => {
  const flat = [];
  for (const phase of category?.phases || []) {
    for (const block of phase.blocks || []) {
      if (block.fixtures) flat.push(...block.fixtures);
      for (const round of block.rounds || []) {
        if (round.fixtures) flat.push(...round.fixtures);
      }
    }
  }
  // Category root-level fixtures (legacy/auto-generated) so the count matches
  // everything the fixtures page can display.
  if (Array.isArray(category?.fixtures)) flat.push(...category.fixtures);
  return flat;
};

const collectFixturesForTeam = (fixtureList, teamId) => {
  if (!teamId || !Array.isArray(fixtureList)) return { played: 0, upcoming: 0 };
  const idStr = teamId.toString();
  let played = 0;
  let upcoming = 0;
  for (const f of fixtureList) {
    if (!fixtureInvolvesTeam(f, idStr)) continue;
    if (fixtureIsPlayed(f)) played += 1;
    else upcoming += 1;
  }
  return { played, upcoming };
};

const SwapTeamsModal = ({ isOpen, onClose, onSuccess, fixtures }) => {
  const dispatch = useDispatch();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );

  const [outgoingId, setOutgoingId] = useState("");
  const [incomingId, setIncomingId] = useState("");
  const [mode, setMode] = useState("INHERIT");
  const [winnerScore, setWinnerScore] = useState("3");
  const [loserScore, setLoserScore] = useState("0");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setOutgoingId("");
      setIncomingId("");
      setMode("INHERIT");
      setWinnerScore("3");
      setLoserScore("0");
    }
  }, [isOpen]);

  const teamOptions = useMemo(() => {
    return (selectedCategory?.teams || [])
      .filter((t) => t?.teamId)
      .map((t) => {
        const team = t.teamId;
        const id = (team?._id || team?.id || team)?.toString?.();
        return {
          value: id,
          label: team?.title || team?.name || "Untitled team",
        };
      });
  }, [selectedCategory?.teams]);

  // The fixtures the page derived and displays — single source of truth for
  // both the played/upcoming count and "does this team have fixtures yet".
  // Fall back to walking the category only if they weren't passed.
  const fixtureList = useMemo(
    () =>
      Array.isArray(fixtures) && fixtures.length
        ? fixtures
        : flattenCategoryFixtures(selectedCategory),
    [fixtures, selectedCategory]
  );

  const teamHasFixtures = useCallback(
    (teamId) => {
      const idStr = teamId?.toString?.();
      if (!idStr) return false;
      return fixtureList.some((f) => fixtureInvolvesTeam(f, idStr));
    },
    [fixtureList]
  );

  // Outgoing = a team that's actually in the competition (has fixtures).
  // Incoming = a team registered to the category but not slotted into any
  // fixture yet. This restricts the action to dropping a fresh team in to
  // replace a current one — never merging two teams that both already play.
  const outgoingOptions = useMemo(
    () => teamOptions.filter((o) => teamHasFixtures(o.value)),
    [teamOptions, teamHasFixtures]
  );

  const incomingOptions = useMemo(
    () => teamOptions.filter((o) => !teamHasFixtures(o.value)),
    [teamOptions, teamHasFixtures]
  );

  const outgoingSelected = useMemo(
    () => outgoingOptions.find((o) => o.value === outgoingId) || null,
    [outgoingOptions, outgoingId]
  );

  const incomingSelected = useMemo(
    () => incomingOptions.find((o) => o.value === incomingId) || null,
    [incomingOptions, incomingId]
  );

  const impact = useMemo(
    () => collectFixturesForTeam(fixtureList, outgoingId),
    [fixtureList, outgoingId]
  );

  const canSubmit =
    !!outgoingId &&
    !!incomingId &&
    outgoingId !== incomingId &&
    !isSubmitting &&
    (mode !== "FORFEIT" || (winnerScore !== "" && loserScore !== ""));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    const loadingToast = toast.loading("Swapping teams...");
    try {
      const payload = {
        outgoingActualTeamId: outgoingId,
        incomingActualTeamId: incomingId,
        mode,
        ...(mode === "FORFEIT"
          ? {
              forfeitScore: {
                winner: Number(winnerScore),
                loser: Number(loserScore),
              },
            }
          : {}),
      };
      await dispatch(
        swapTeamInCategory({
          tournamentId: selectedTournament?.id || selectedTournament?._id,
          categoryId: selectedCategory?._id || selectedCategory?.id,
          payload,
        })
      ).unwrap();
      toast.dismiss(loadingToast);
      toast.success("Teams swapped");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.message || "Failed to replace teams");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ClubbieModal isOpen={isOpen} toggle={onClose} title="Team replacement">
      <div className="flex flex-col gap-5">
        {/* Outgoing team */}
        <div className="flex flex-col gap-2">
          <FieldLabel>Outgoing team</FieldLabel>
          <ClubbieSelect
            placeholder="Pick a team currently in the fixtures"
            options={outgoingOptions}
            value={outgoingSelected}
            onChange={(opt) => setOutgoingId(opt?.value || "")}
            getOptionLabel={(o) => o?.label || ""}
            getOptionValue={(o) => o?.value || ""}
            isSearchable
          />
          {outgoingId && (
            <p className="text-xs text-[#7F8585] m-0">
              {impact.played} played · {impact.upcoming} upcoming fixture
              {impact.played + impact.upcoming === 1 ? "" : "s"} across the category
            </p>
          )}
        </div>

        {/* Incoming team — only teams with no fixtures yet. The expected flow is:
            admin invites the new team to the category, they accept, then they're
            dropped in here to take over a current team's fixtures. */}
        <div className="flex flex-col gap-2">
          <FieldLabel>Incoming team</FieldLabel>
          <ClubbieSelect
            placeholder="Pick a replacement team with no fixtures"
            options={incomingOptions}
            value={incomingSelected}
            onChange={(opt) => setIncomingId(opt?.value || "")}
            getOptionLabel={(o) => o?.label || ""}
            getOptionValue={(o) => o?.value || ""}
            isSearchable
          />
          <p className="text-xs text-[#7F8585] m-0">
            Only teams in this category that aren't in any fixture yet can be
            picked. Invite the replacement team first if they aren't listed.
          </p>
        </div>

        {/* Mode — only relevant when there's something to inherit/expunge/forfeit.
            With 0 played fixtures all three modes are equivalent (B simply takes
            over upcoming fixtures), so we hide the picker entirely. */}
        {outgoingId && impact.played > 0 && (
          <div className="flex flex-col gap-3">
            <label className="block text-[14px] font-normal text-clubbie-white">Mode</label>
            <div className="flex flex-col gap-[6px]">
              {MODES.map((m) => (
                <label
                  key={m.value}
                  className="flex items-start gap-[12px] cursor-pointer"
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      mode === m.value ? "border-[#00E7FF]" : "border-[#333E48]"
                    }`}
                  >
                    {mode === m.value && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />
                    )}
                  </div>
                  <input
                    type="radio"
                    name="swap-mode"
                    className="hidden"
                    checked={mode === m.value}
                    onChange={() => setMode(m.value)}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm text-[#FEFCEB]">{m.label}</span>
                    <span className="text-xs text-[#7F8585]">{m.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Forfeit score */}
        {outgoingId && impact.played > 0 && mode === "FORFEIT" && (
          <div className="flex flex-col gap-2">
            <FieldLabel>Forfeit score (opponent vs outgoing team)</FieldLabel>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                value={winnerScore}
                onChange={(e) => setWinnerScore(e.target.value)}
                className="w-20 h-11 px-3 bg-app-surface border border-app-surface rounded-full text-center text-[14px] text-[#FEFCEB] focus:outline-none focus:border-[#00E7FF]"
              />
              <span className="text-[#7F8585] text-sm">vs</span>
              <input
                type="number"
                min="0"
                value={loserScore}
                onChange={(e) => setLoserScore(e.target.value)}
                className="w-20 h-11 px-3 bg-app-surface border border-app-surface rounded-full text-center text-[14px] text-[#FEFCEB] focus:outline-none focus:border-[#00E7FF]"
              />
            </div>
            <p className="text-xs text-[#7F8585] m-0">
              Applied to each of the outgoing team's played fixtures in favour of
              the opponent.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                <span>Swapping…</span>
              </>
            ) : (
              "Replace teams"
            )}
          </button>
        </div>
      </div>
    </ClubbieModal>
  );
};

export default SwapTeamsModal;
